import React, { useState, useRef, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import { PlusIcon, Bars3Icon, PencilIcon } from '@heroicons/react/24/outline';
import { FormTemplate, FormField, FormSection } from '../../types/formBuilder';
import { useLanguage } from '../../contexts/LanguageContext';
import FieldRenderer from './FieldRenderer';
import SectionRenderer from './SectionRenderer';
import { createFieldFromType } from './fieldUtils';

interface FormCanvasProps {
  template: FormTemplate;
  selectedField?: string;
  onSelectField: (fieldId: string) => void;
  onUpdateField: (fieldId: string, updates: Partial<FormField>) => void;
  onDeleteField: (fieldId: string) => void;
  onAddSection: (section: FormSection) => void;
  onAddField: (field: FormField, sectionId?: string) => void;
  onUpdateSection: (sectionId: string, updates: Partial<FormSection>) => void;
  onDeleteSection: (sectionId: string) => void;
  onUpdateTemplate: (updates: Partial<FormTemplate>) => void;
}

const FormCanvas: React.FC<FormCanvasProps> = ({
  template,
  selectedField,
  onSelectField,
  onUpdateField,
  onDeleteField,
  onAddSection,
  onAddField,
  onUpdateSection,
  onDeleteSection,
  onUpdateTemplate
}) => {
  const { t } = useLanguage();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(template.name);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [isCreatingSection, setIsCreatingSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const sectionInputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Focus section input when creating starts
  useEffect(() => {
    if (isCreatingSection && sectionInputRef.current) {
      sectionInputRef.current.focus();
    }
  }, [isCreatingSection]);

  // Update local title when template changes
  useEffect(() => {
    setTitleValue(template.name);
  }, [template.name]);

  const handleTitleSave = () => {
    const newTitle = titleValue.trim();
    if (newTitle && newTitle !== template.name && newTitle.length <= 50) {
      onUpdateTemplate({ name: newTitle });
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setTitleValue(template.name);
      setIsEditingTitle(false);
    }
  };

  const handleSectionSave = () => {
    const sectionName = newSectionName.trim();
    if (sectionName) {
      const newSection = {
        id: `section_${Date.now()}`,
        name: sectionName.toLowerCase().replace(/\s+/g, '_'),
        label: sectionName,
        order: sortedSections.length,
        color: 'primary'
      };
      onAddSection(newSection);
    }
    setNewSectionName('');
    setIsCreatingSection(false);
  };

  const handleSectionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSectionSave();
    } else if (e.key === 'Escape') {
      setNewSectionName('');
      setIsCreatingSection(false);
    }
  };

  const handleStartCreatingSection = () => {
    setIsCreatingSection(true);
    setNewSectionName('');
  };

  const [{ isOver }, drop] = useDrop({
    accept: 'FIELD',
    drop: (item: { fieldType: { type: string } }, monitor) => {
      // Only handle drop if no child handled it (i.e., dropped outside sections)
      const dropResult = monitor.getDropResult() as { sectionId?: string } | null;
      if (!dropResult || !dropResult.sectionId) {
        handleFieldDrop(item.fieldType);
      }
    },
    collect: (monitor: any) => ({
      isOver: monitor.isOver(),
    }),
  });

  const handleFieldDrop = (fieldType: { type: string }, sectionId?: string) => {
    const newField = createFieldFromType(fieldType.type as any, t, sectionId);
    onAddField(newField, sectionId);
  };

  const handleMoveSectionUp = (sectionId: string) => {
    const sectionIndex = sortedSections.findIndex(s => s.id === sectionId);
    if (sectionIndex > 0) {
      const currentSection = sortedSections[sectionIndex];
      const previousSection = sortedSections[sectionIndex - 1];
      
      // Swap orders
      onUpdateSection(currentSection.id, { order: previousSection.order });
      onUpdateSection(previousSection.id, { order: currentSection.order });
    }
  };

  const handleMoveSectionDown = (sectionId: string) => {
    const sectionIndex = sortedSections.findIndex(s => s.id === sectionId);
    if (sectionIndex < sortedSections.length - 1) {
      const currentSection = sortedSections[sectionIndex];
      const nextSection = sortedSections[sectionIndex + 1];
      
      // Swap orders
      onUpdateSection(currentSection.id, { order: nextSection.order });
      onUpdateSection(nextSection.id, { order: currentSection.order });
    }
  };

  const handleMoveSection = (dragIndex: number, hoverIndex: number) => {
    if (dragIndex === hoverIndex) return;
    
    // Create a new sections array with reordered items
    const newSections = [...template.sections];
    const draggedSection = newSections.find((_, index) => {
      // Find section by current sorted position
      return sortedSections[dragIndex]?.id === _.id;
    });
    
    if (!draggedSection) return;
    
    // Remove dragged section
    const filteredSections = newSections.filter(s => s.id !== draggedSection.id);
    
    // Insert at new position based on sorted sections
    const targetSection = sortedSections[hoverIndex];
    const targetIndex = filteredSections.findIndex(s => s.id === targetSection?.id);
    
    if (targetIndex >= 0) {
      filteredSections.splice(targetIndex, 0, draggedSection);
    } else {
      filteredSections.push(draggedSection);
    }
    
    // Update orders
    const updatedSections = filteredSections.map((section, index) => ({
      ...section,
      order: index
    }));
    
    // Update template with new sections
    onUpdateTemplate({ sections: updatedSections });
  };

  const handleMoveField = (dragIndex: number, hoverIndex: number, targetSectionId: string, sourceSectionId?: string) => {
    // Cross-section move (including from/to default section)
    if (sourceSectionId && sourceSectionId !== targetSectionId) {
      // Get source section fields (handle default section)
      const sourceFields = template.fields.filter(field => {
        const fieldSection = field.section || 'default';
        return fieldSection === sourceSectionId;
      });
      const sortedSourceFields = sourceFields.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      if (dragIndex >= sortedSourceFields.length) return;
      
      const draggedField = sortedSourceFields[dragIndex];
      
      // Get target section fields (handle default section)
      const targetFields = template.fields.filter(field => {
        const fieldSection = field.section || 'default';
        return fieldSection === targetSectionId;
      });
      const sortedTargetFields = targetFields.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      // Remove field from source section and update orders
      const updatedSourceFields = sortedSourceFields
        .filter((_, index) => index !== dragIndex)
        .map((field, index) => ({ ...field, order: index }));
      
      // Add field to target section at specified position
      const newTargetFields = [...sortedTargetFields];
      const insertIndex = Math.min(hoverIndex, newTargetFields.length);
      
      // Set the correct section (null for default, actual ID for real sections)
      const newSection = targetSectionId === 'default' ? undefined : targetSectionId;
      newTargetFields.splice(insertIndex, 0, { ...draggedField, section: newSection });
      
      // Update orders for target section
      const updatedTargetFields = newTargetFields.map((field, index) => ({
        ...field,
        order: index
      }));
      
      // Combine all fields
      const allFields: FormField[] = [];
      
      // Add all fields that are not in source or target sections
      template.fields.forEach(field => {
        const fieldSection = field.section || 'default';
        if (fieldSection !== sourceSectionId && fieldSection !== targetSectionId) {
          allFields.push(field);
        }
      });
      
      // Add updated source section fields (excluding the moved field)
      updatedSourceFields.forEach(field => {
        allFields.push(field);
      });
      
      // Add updated target section fields (including the moved field)
      updatedTargetFields.forEach(field => {
        allFields.push(field);
      });
      
      onUpdateTemplate({ fields: allFields });
      return;
    }
    
    // Same section reordering (including default section)
    if (dragIndex === hoverIndex) return;
    
    const sectionFields = template.fields.filter(field => {
      const fieldSection = field.section || 'default';
      return fieldSection === targetSectionId;
    });
    const sortedSectionFields = sectionFields.sort((a, b) => (a.order || 0) - (b.order || 0));
    
    if (dragIndex >= sortedSectionFields.length || hoverIndex >= sortedSectionFields.length) return;
    
    const newFields = [...sortedSectionFields];
    const draggedField = newFields[dragIndex];
    
    newFields.splice(dragIndex, 1);
    newFields.splice(hoverIndex, 0, draggedField);
    
    const updatedFields = newFields.map((field, index) => ({
      ...field,
      order: index
    }));
    
    const allFields = template.fields.map(field => {
      const fieldSection = field.section || 'default';
      if (fieldSection === targetSectionId) {
        const updatedField = updatedFields.find(uf => uf.id === field.id);
        return updatedField || field;
      }
      return field;
    });
    
    onUpdateTemplate({ fields: allFields });
  };

  // Group fields by section
  const fieldsBySection = template.fields.reduce((acc, field) => {
    const sectionId = field.section || 'default';
    if (!acc[sectionId]) {
      acc[sectionId] = [];
    }
    acc[sectionId].push(field);
    return acc;
  }, {} as Record<string, FormField[]>);

  // Sort sections by order
  const sortedSections = [...template.sections].sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleAddSection = () => {
    handleStartCreatingSection();
  };

  return (
    <div
      ref={drop}
      className={`
        min-h-full p-6
        ${isOver ? 'bg-primary-50 dark:bg-primary-900/20' : ''}
      `}
    >
      <div className="max-w-4xl mx-auto">
        {/* Editable Form Title */}
        <div className="mb-8 border-b border-gray-200 dark:border-gray-700 pb-6">
          <div className="flex items-center space-x-3 group">
            {isEditingTitle ? (
              <div className="flex-1">
                <input
                  ref={titleInputRef}
                  type="text"
                  value={titleValue}
                  onChange={(e) => setTitleValue(e.target.value)}
                  onBlur={handleTitleSave}
                  onKeyDown={handleTitleKeyDown}
                  maxLength={50}
                  className="text-3xl font-bold bg-transparent border-2 border-primary-300 dark:border-primary-600 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 dark:focus:border-primary-400 w-full"
                  placeholder={t('formBuilder.untitledForm') || 'Untitled Form'}
                />
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                  {titleValue.length}/50
                </div>
              </div>
            ) : (
              <>
                <h1 
                  className="text-3xl font-bold text-gray-900 dark:text-gray-100 cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex-1"
                  onClick={() => setIsEditingTitle(true)}
                  title={t('formBuilder.clickToEdit') || 'Click to edit'}
                >
                  {template.name || t('formBuilder.untitledForm') || 'Untitled Form'}
                </h1>
                <button
                  onClick={() => setIsEditingTitle(true)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                  title={t('formBuilder.editTitle') || 'Edit title'}
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
          
          {template.description && (
            <p className="mt-3 text-gray-600 dark:text-gray-400">
              {template.description}
            </p>
          )}
        </div>

        {/* Unassigned Fields Section */}
        {fieldsBySection.default && fieldsBySection.default.length > 0 && (
          <div className="mb-8">
            <div className="bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                    {t('formBuilder.unassignedFields') || 'Unassigned Fields'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('formBuilder.unassignedFieldsDescription') || 'Fields not assigned to any section - drag them to sections'}
                  </p>
                </div>
                <span className="bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-full text-xs font-medium">
                  {fieldsBySection.default.length} {fieldsBySection.default.length === 1 ? 'field' : 'fields'}
                </span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {fieldsBySection.default
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((field, index) => (
                    <FieldRenderer
                      key={field.id}
                      field={field}
                      isSelected={selectedField === field.id}
                      onSelect={() => onSelectField(field.id)}
                      onUpdate={(updates) => onUpdateField(field.id, updates)}
                      onDelete={() => onDeleteField(field.id)}
                      onMoveField={handleMoveField}
                      fieldIndex={index}
                      sectionId="default"
                      isPreview={false}
                    />
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Sections */}
        {sortedSections.map((section, index) => (
          <SectionRenderer
            key={section.id}
            section={section}
            fields={fieldsBySection[section.id] || []}
            selectedField={selectedField}
            onSelectField={onSelectField}
            onUpdateField={onUpdateField}
            onDeleteField={onDeleteField}
            onUpdateSection={onUpdateSection}
            onDeleteSection={onDeleteSection}
            onDropField={(fieldType, sectionId) => handleFieldDrop(fieldType, sectionId)}
            onMoveSectionUp={handleMoveSectionUp}
            onMoveSectionDown={handleMoveSectionDown}
            onMoveSection={handleMoveSection}
            onMoveField={handleMoveField}
            sectionIndex={index}
            canMoveUp={index > 0}
            canMoveDown={index < sortedSections.length - 1}
          />
        ))}

        {/* Add Section Button/Input */}
        <div className="mb-8">
          {isCreatingSection ? (
            <div className="w-full p-4 border-2 border-primary-300 dark:border-primary-600 
                          rounded-lg bg-primary-50 dark:bg-primary-900/20">
              <div className="flex items-center space-x-3">
                <PlusIcon className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                <input
                  ref={sectionInputRef}
                  type="text"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  onBlur={handleSectionSave}
                  onKeyDown={handleSectionKeyDown}
                  placeholder={t('formBuilder.sectionNamePlaceholder') || 'Enter section name...'}
                  className="flex-1 bg-transparent border-none outline-none text-lg font-medium 
                           text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
            </div>
          ) : (
            <button
              onClick={handleAddSection}
              className="w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 
                       rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 
                       transition-colors group"
            >
              <div className="flex items-center justify-center space-x-2 text-gray-500 dark:text-gray-400 
                            group-hover:text-primary-600">
                <PlusIcon className="h-5 w-5" />
                <span>{t('formBuilder.addSection') || 'Add Section'}</span>
              </div>
            </button>
          )}
        </div>

        {/* Drop Zone */}
        {template.fields.length === 0 && (
          <div className="text-center py-12">
            <Bars3Icon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('formBuilder.emptyForm') || 'No fields yet'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('formBuilder.emptyFormDescription') || 'Drag fields from the palette to get started'}
            </p>
          </div>
        )}

        {/* Form Actions Preview */}
        <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-600">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                       text-sm font-medium text-gray-700 dark:text-gray-300 
                       bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              {template.settings.cancelButtonText || 'Cancel'}
            </button>
            <button
              type="button"
              className="px-4 py-2 bg-primary-600 border border-transparent rounded-md 
                       text-sm font-medium text-white hover:bg-primary-700"
            >
              {template.settings.submitButtonText || 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


export default FormCanvas;
